// src/shared/components/ui/button.tsx

import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'

import { Spinner } from '@/shared/components/ui/spinner'
import { cn } from '@/shared/utils/cn'

export const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-bg)]',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-brand)] text-[var(--color-brand-fg)] hover:brightness-105 active:brightness-95 focus-visible:ring-[var(--color-brand)]',
        secondary:
          'bg-[var(--panel-subtle)] text-[var(--color-text)] border border-[var(--panel-border)] hover:bg-[var(--panel-border)] focus-visible:ring-[var(--color-brand)]',
        ghost:
          'bg-transparent text-[var(--color-text-soft)] hover:text-[var(--color-text)] hover:bg-[var(--panel-subtle)] focus-visible:ring-[var(--color-brand)]',
        danger:
          'bg-[var(--color-danger)] text-white hover:brightness-105 focus-visible:ring-[var(--color-danger)]',
        link: 'bg-transparent text-[var(--color-brand)] underline-offset-2 hover:underline focus-visible:ring-[var(--color-brand)]',
      },
      size: {
        xs: 'h-7 px-2.5 text-xs',
        sm: 'h-8 px-3',
        md: 'h-9 px-3.5',
        lg: 'h-10 px-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean
}

export function Button({
  className,
  variant,
  size,
  loading,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      <span className={cn('contents', loading && 'invisible')}>{children}</span>
      {loading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner size={size === 'xs' || size === 'sm' ? 'xs' : 'sm'} />
        </span>
      ) : null}
    </button>
  )
}
